import React, { useEffect, useRef, useState } from 'react';

const AD_KEY = '62c309310a6df4b51b2fe6ea068a9284';
const AD_WIDTH = 728;
const AD_HEIGHT = 90;
// The ad network gives no success callback, so we give it this long to
// render something before we give up and collapse the slot.
const LOAD_TIMEOUT_MS = 4000;

/**
 * Top-of-page banner ad. Rendered inside its own sandboxed iframe rather
 * than injected straight into the page: this style of ad tag commonly
 * calls document.write, which (if it ran in the main document) would wipe
 * out the whole React app the moment it fired, since the script is added
 * long after the page has already loaded. Confining it to an iframe means
 * a document.write only clears that iframe's document, not ours.
 *
 * If the ad never produces visible content (network rejects the key,
 * empty response, blocked by an ad blocker, etc.) the slot collapses
 * instead of leaving a blank gap.
 */
export default function BannerAd() {
  const iframeRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let resolved = false;
    let timer;

    const fail = () => {
      if (resolved) return;
      resolved = true;
      window.clearTimeout(timer);
      setFailed(true);
    };

    const doc = iframe.contentDocument;
    if (!doc) {
      fail();
      return undefined;
    }

    doc.open();
    doc.write('<!doctype html><html><head></head><body style="margin:0"></body></html>');
    doc.close();

    const optionsScript = doc.createElement('script');
    optionsScript.type = 'text/javascript';
    optionsScript.text = `atOptions = {
      'key' : '${AD_KEY}',
      'format' : 'iframe',
      'height' : ${AD_HEIGHT},
      'width' : ${AD_WIDTH},
      'params' : {}
    };`;

    const invokeScript = doc.createElement('script');
    invokeScript.type = 'text/javascript';
    invokeScript.src = `https://www.highrevenueformat.com/${AD_KEY}/invoke.js`;
    invokeScript.async = true;
    // Fires on hard network failures (DNS error, connection refused, an ad
    // blocker cancelling the request). A 403/empty response still "loads"
    // successfully as far as the browser is concerned, so this alone isn't
    // enough — the timeout below catches those cases.
    invokeScript.onerror = fail;

    doc.body.appendChild(optionsScript);
    doc.body.appendChild(invokeScript);

    timer = window.setTimeout(() => {
      if (resolved) return;
      resolved = true;
      const rendered = doc.body.querySelector('iframe, ins, img, div');
      if (!rendered) setFailed(true);
    }, LOAD_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  if (failed) return null;

  return (
    <div className="banner-ad">
      <iframe
        ref={iframeRef}
        title="advertisement"
        width={AD_WIDTH}
        height={AD_HEIGHT}
        scrolling="no"
        frameBorder="0"
        sandbox="allow-scripts allow-same-origin allow-popups"
        style={{ border: 0, width: AD_WIDTH, height: AD_HEIGHT }}
      />
    </div>
  );
}
